import React, { useState } from 'react';
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import { extractHHMM } from '../Helpers/dateFns';
import { restrictClinicSchedule, removeClinicRestriction } from '../../http/clinics';

const ScheduleContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: #ff6b6b;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${isMobile ? '15px' : '20px'};
  line-height: 1;

  &:hover {
    background-color: #ff5252;
  }

  &:before {
    content: '×';
  }
`;

const Button = styled.button`
  padding: 5px 10px;
  background-color: ${({ colours }) => colours[0]};
  color: white;
  font-size: 11px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  width:100%;
  transition: background-color 0.3s ease;


  &:hover {
    background-color: ${({ colours }) => colours[1]};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const TimeSlot = styled.div`
  display: flex;
  border-top: 1px solid #ddd;
  position: relative;
  padding: 5px 0;
`;

const TimeLabel = styled.div`
  width: 60px;
  text-align: right;
  padding-right: 10px;
  font-size: 0.9rem;
`;

const Event = styled.div`
  background-color: ${(props) => props.type === 'Restriction' ? '#ff6b6b' : (props.type === 'Disabled' ? 'rgb(224, 224, 224)' : '#add8e6')};
  cursor: pointer;
  font-size: 0.9rem;
  padding: 5px;
  border-radius: 4px;
  margin-left: 10px;
  width: ${(props) => props.source === 'Opticians' ? '100%' : '85%' };

  &:hover {
    background-color: ${(props) => props.type === 'Restriction' ? '#ff5252' : (props.type === 'Disabled' ? 'rgb(209, 209, 209)' : '#86c5da')};
  }
`;

const SelectInputStyled = styled.select`
  padding: 5px;
  margin-left: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  width:100%;
  background-color: ${props => props.missing ? '#ffdddd' : '#fff'};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;


const FreeSlot = styled.div`
  background-color:rgb(224, 224, 224);
  cursor: pointer;
  font-size: 0.9rem;
  padding: 5px;
  border-radius: 4px;
  margin-left: 10px;
  width:100%;

  &:hover {
    background-color: rgb(209, 209, 209);
  }
`;

const SlotButton = styled.button`
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
  &:hover {
    background-color: #e7e7e7;
  }
`;


const generateTimeSlots = (startTime, endTime, interval) => {
  const start = parseInt(startTime.replace(':', ''), 10);
  const end = parseInt(endTime.replace(':', ''), 10);
  let currentTime = start;
  const slots = [];

  while (currentTime <= end) {
    const hours = Math.floor(currentTime / 100);
    const minutes = currentTime % 100;
    slots.push(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
    currentTime += interval;
    if (currentTime % 100 >= 60) {
      currentTime = (Math.floor(currentTime / 100) + 1) * 100 + (currentTime % 100 - 60);
    }
  }

  return slots.slice(0, slots.length - 1);
};

export function OpticiansSchedule({
  events,
  startTime,
  endTime,
  buttonFn,
  interval }) {

  const [currentEyeTicket, setCurrentEyeTicket] = useState(null);

  if (interval === 0) { return; }
  const timeSlots = generateTimeSlots(startTime, endTime, interval);
  const hasEvent = (time) => {
    return events.some(event => extractHHMM(event.ScheduledAppointmentDateTime, true) === time && event.IsBlocked);
  };

  return (
    <ScheduleContainer>
      {timeSlots.map((time, index) => (
        <TimeSlot key={index}>
          <TimeLabel>{time}</TimeLabel>
          {!hasEvent(time) ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '90%' }}>
                <FreeSlot>Free</FreeSlot>
              </div>
            </>
          ) : (
            events.map((event, eventIndex) => {
              if (time === extractHHMM(event.ScheduledAppointmentDateTime, true)) {
                if (event.EyeticketID !== 0) {
                  return (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '90%' }}>
                        <Event key={eventIndex} type={event.Status !== "Open" ? 'Disabled' : ''} source='Opticians'>
                          <b>Appointment: </b>{event.Appointment}
                          <br />
                          <b>Frame: </b>{event.Frame}
                          <br />
                          <b>Status: </b>{event.Status}
                        </Event>
                        {event.Status === "Open" && (
                          <SelectInputStyled
                            onChange={(changeEvent) => {
                              const selectedValue = changeEvent.target.value;
                              buttonFn(selectedValue, event);
                            }}
                          >
                            <option value="">Select</option>
                            {event.DropDownValue?.split(',').map(option => (
                              <option key={option.trim()} value={option.trim()}>{option.trim()}</option>
                            ))}
                          </SelectInputStyled>
                        )}
                      </div>

                    </>
                  );
                } else if (event.Appointment === 'Restricted') {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '90%' }}>
                      <Event key={eventIndex} type={'Restriction'} source='Opticians'>
                        <b></b>{event.Appointment}
                      </Event>
                    </div>
                  );
                }
              }
              return null;
            })
          )}
        </TimeSlot>
      ))
      }
    </ScheduleContainer >
  );
};

const Schedule = ({
  events,
  startTime,
  endTime,
  interval,
  buttonFn,
  onDeleteEyeticket,
  onDeleteRestriction,
  onAddRestriction,
  selectedSchedule,
  isLoading,
  setIsLoading }) => {

  const timeSlots = generateTimeSlots(startTime, endTime, interval);
  const hasEvent = (time) => {
    return events.some(event => extractHHMM(event.ScheduledAppointmentDateTime, true) === time && event.IsBlocked);
  };

  return (
    <ScheduleContainer>
      {timeSlots.map((time, index) => (
        <TimeSlot key={index}>
          <TimeLabel>{time}</TimeLabel>
          {!hasEvent(time) && buttonFn ? (
            <>
              <SlotButton disabled={isLoading} onClick={() => {
                buttonFn(true);
                selectedSchedule(
                  time
                );
              }
              }>
                Book Appointment
              </SlotButton>
              <SlotButton 
              onClick={() => onAddRestriction(time)}
              disabled={isLoading}>
                Restrict
              </SlotButton>
            </>
          ) : (
            events.map((event, eventIndex) => {
              if (time === extractHHMM(event.ScheduledAppointmentDateTime, true)) {
                if (event.EyeticketID !== 0) {
                  return (
                    <>
                      <Event key={eventIndex} onClick={() => buttonFn(false, false, event.EyeticketID)}>
                        <b>Appointment: </b>{event.Appointment}
                        <br></br>
                        <b>Frame: </b>{event.Frame}
                        <br></br>
                        <b>Status: </b>{event.Status}
                      </Event>
                      {onDeleteEyeticket && (
                        <DeleteButton disabled={isLoading} onClick={() => onDeleteEyeticket(event.EyeticketID)} title="Cancel Appointment" />
                      )}
                    </>
                  );
                } else if (event.Appointment === 'Restricted') {
                  return (
                    <>
                      <Event key={eventIndex} type={'Restriction'}>
                        <b></b>{event.Appointment}
                      </Event>
                      {onDeleteRestriction && (
                        <DeleteButton disabled={isLoading} onClick={() => onDeleteRestriction(event.restrictionsId)} title="Cancel Restriction" />
                      )}
                    </>
                  );
                }
              }
              return null;
            })
          )}
        </TimeSlot>
      ))}
    </ScheduleContainer>
  );
};


export default Schedule;
